/**
 * UnreadBadge Component
 *
 * Displays an unread message count badge with:
 * - Count display with configurable max count (99+)
 * - Animation on count update (pop/scale effect)
 * - Multiple size variants
 * - Accessible color contrast
 *
 * @module components/chat/UnreadBadge
 */

import { memo, useEffect, useState, useRef } from 'react';

// ============================================
// Constants
// ============================================

/**
 * Default maximum count before showing "+"
 */
const DEFAULT_MAX_COUNT = 99;

/**
 * Animation duration in milliseconds
 */
const ANIMATION_DURATION_MS = 300;

/**
 * Size variants configuration
 */
const SIZE_VARIANTS = {
  xs: {
    container: 'min-w-[14px] h-[14px] text-[9px] px-0.5',
    dot: 'w-1.5 h-1.5'
  },
  sm: {
    container: 'min-w-[16px] h-4 text-[10px] px-1',
    dot: 'w-2 h-2'
  },
  md: {
    container: 'min-w-[20px] h-5 text-xs px-1.5',
    dot: 'w-2.5 h-2.5'
  },
  lg: {
    container: 'min-w-[24px] h-6 text-sm px-2',
    dot: 'w-3 h-3'
  }
};

/**
 * Variant color configurations
 */
const VARIANT_COLORS = {
  danger: {
    bg: 'bg-red-500',
    text: 'text-white',
    ring: 'ring-red-400'
  },
  primary: {
    bg: 'bg-indigo-500',
    text: 'text-white',
    ring: 'ring-indigo-400'
  },
  warning: {
    bg: 'bg-yellow-500',
    text: 'text-gray-900',
    ring: 'ring-yellow-400'
  },
  success: {
    bg: 'bg-green-500',
    text: 'text-white',
    ring: 'ring-green-400'
  },
  secondary: {
    bg: 'bg-gray-500',
    text: 'text-white',
    ring: 'ring-gray-400'
  }
};

// ============================================
// Custom Animation Styles
// ============================================

/**
 * CSS keyframes for custom pop animation
 * Injects styles into document head once
 */
const CUSTOM_ANIMATION_CSS = `
@keyframes unread-badge-pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
}

@keyframes unread-badge-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px) rotate(-3deg); }
  75% { transform: translateX(2px) rotate(3deg); }
}

.animate-unread-pop {
  animation: unread-badge-pop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.animate-unread-shake {
  animation: unread-badge-shake 0.3s ease-out;
}
`;

let stylesInjected = false;

/**
 * Inject custom animation styles into document head (runs once)
 */
function injectAnimationStyles() {
  if (stylesInjected || typeof document === 'undefined') return;

  const existingStyle = document.querySelector('[data-unread-badge-styles]');
  if (existingStyle) {
    stylesInjected = true;
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.setAttribute('data-unread-badge-styles', '');
  styleElement.textContent = CUSTOM_ANIMATION_CSS;
  document.head.appendChild(styleElement);
  stylesInjected = true;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Format count for display with max limit
 * @param {number} count - The unread count
 * @param {number} maxCount - Maximum count before showing "+"
 * @returns {string} Formatted count string
 */
function formatCount(count, maxCount) {
  if (count <= 0) return '';
  if (count > maxCount) return `${maxCount}+`;
  return count.toString();
}

// ============================================
// Animation Hook
// ============================================

/**
 * Hook to manage badge animation state on count changes
 * @param {number} count - Current count
 * @param {number} animationDuration - Duration of animation in ms
 * @returns {boolean} Whether animation is active
 */
function useBadgeAnimation(count, animationDuration = ANIMATION_DURATION_MS) {
  const [isAnimating, setIsAnimating] = useState(false);
  const prevCountRef = useRef(count);
  const animationTimeoutRef = useRef(null);

  // Inject custom animation styles on mount
  useEffect(() => {
    injectAnimationStyles();
  }, []);

  useEffect(() => {
    // Only animate when count increases
    if (count > prevCountRef.current && count > 0) {
      // Clear any existing animation timeout
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      setIsAnimating(true);

      // Reset animation after duration
      animationTimeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
      }, animationDuration);
    }

    prevCountRef.current = count;

    // Cleanup timeout on unmount
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [count, animationDuration]);

  return isAnimating;
}

// ============================================
// Sub-Components
// ============================================

/**
 * Animated dot indicator for unread presence (no count)
 * @param {object} props - Component props
 * @param {string} props.size - Size variant
 * @param {string} props.variant - Color variant
 * @param {boolean} props.pulse - Whether to show pulse animation
 * @returns {React.ReactElement} Dot indicator
 */
const UnreadDot = memo(function UnreadDot({
  size = 'md',
  variant = 'danger',
  pulse = false
}) {
  const sizeClass = SIZE_VARIANTS[size]?.dot || SIZE_VARIANTS.md.dot;
  const colors = VARIANT_COLORS[variant] || VARIANT_COLORS.danger;

  return (
    <span
      className={`
        inline-flex rounded-full
        ${sizeClass}
        ${colors.bg}
        ${pulse ? 'animate-pulse' : ''}
      `}
      role="status"
      aria-label="Unread messages"
    />
  );
});

// ============================================
// Main Component
// ============================================

/**
 * UnreadBadge Component
 *
 * Displays unread count with animation on update.
 *
 * @param {object} props - Component props
 * @param {number} props.count - Unread message count
 * @param {number} [props.maxCount=99] - Maximum count before showing "+"
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Size variant
 * @param {'danger'|'primary'|'warning'|'success'} [props.variant='danger'] - Color variant
 * @param {boolean} [props.showZero=false] - Whether to show badge when count is 0
 * @param {boolean} [props.dot=false] - Show dot instead of count
 * @param {boolean} [props.pulse=false] - Whether to show constant pulse animation
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.ariaLabel] - Custom aria-label
 * @returns {React.ReactElement|null} Badge or null if count is 0 and showZero is false
 */
const UnreadBadge = memo(function UnreadBadge({
  count = 0,
  maxCount = DEFAULT_MAX_COUNT,
  size = 'md',
  variant = 'danger',
  showZero = false,
  dot = false,
  pulse = false,
  className = '',
  ariaLabel
}) {
  // Animation state
  const isAnimating = useBadgeAnimation(count);

  // Don't render if count is 0 and showZero is false
  if (count <= 0 && !showZero) {
    return null;
  }

  // Render dot variant
  if (dot && count > 0) {
    return <UnreadDot size={size} variant={variant} pulse={pulse} />;
  }

  // Get size and color classes
  const sizeClass = SIZE_VARIANTS[size]?.container || SIZE_VARIANTS.md.container;
  const colors = VARIANT_COLORS[variant] || VARIANT_COLORS.danger;

  // Format the display count
  const displayCount = formatCount(count, maxCount);

  // Build animation classes - using custom pop animation for better visual effect
  const animationClasses = isAnimating
    ? 'animate-unread-pop'
    : pulse && count > 0
      ? 'animate-pulse'
      : '';

  // Build aria-label
  const label = ariaLabel
    || (count === 0
      ? 'No unread messages'
      : count === 1
        ? '1 unread message'
        : count > maxCount
          ? `More than ${maxCount} unread messages`
          : `${count} unread messages`);

  return (
    <span
      className={`
        inline-flex items-center justify-center
        font-medium rounded-full
        transition-transform duration-200
        ${sizeClass}
        ${colors.bg}
        ${colors.text}
        ${animationClasses}
        ${className}
      `.trim()}
      role="status"
      aria-label={label}
    >
      {displayCount}
    </span>
  );
});

// ============================================
// Static Properties
// ============================================

/**
 * Display name for debugging
 */
UnreadBadge.displayName = 'UnreadBadge';

// ============================================
// Named Exports
// ============================================

export {
  UnreadBadge as default,
  UnreadDot,
  useBadgeAnimation,
  formatCount,
  injectAnimationStyles,
  SIZE_VARIANTS,
  VARIANT_COLORS,
  DEFAULT_MAX_COUNT,
  ANIMATION_DURATION_MS,
  CUSTOM_ANIMATION_CSS
};
